import type { z } from 'zod';

export namespace ts {
    export declare namespace Union {
        export type pick<T extends string, P extends T> = P;

        /** Converts a union type to an intersection type.
         *
         * (A | B | C) → (A & B & C)
         *
         * # Example
         * ```typescript
         * type Merged = UnionToIntersection<{ foo: 'foo' } | { bar: 'bar' }>
         * // => { foo: 'foo', bar: 'bar' }
         * ```
         *
         * Works by distributing the union into contravariant function positions,
         * then inferring the intersection from the merged overload.
         *
         * # Origin
         * @see [The stack overflow post this comes from for more info](https://stackoverflow.com/questions/50374908/transform-union-type-to-intersection-type) */
        export type toIntersection<U> = (U extends any ? (x: U) => void : never) extends (
            x: infer I,
        ) => void
            ? I
            : never;

        /**
         * Extracts the last member of a union (as seen by the TS compiler).
         *
         * Converts union members to overloaded function signatures, then infers
         * the return type — TS resolves overloads last-to-first, giving us one
         * member at a time.
         */
        export type lastOf<U> =
            Union.toIntersection<U extends unknown ? () => U : never> extends () => infer L
                ? L
                : never;

        /** Counts the number of members in a union. */
        type size<U, Acc extends unknown[] = []> = [U] extends [never]
            ? Acc['length']
            : Union.size<Exclude<U, Union.lastOf<U>>, [unknown, ...Acc]>;
    }

    export namespace Object {
        export namespace Keys {
            /** Exclude all types from `T` that contain type `V` */
            export type filter<T, V> = {
                [K in keyof T as T[K] extends V ? never : K]: T[K];
            };

            /** Exclude all keys from `T` that don't contain type `V` */
            export type withType<T, V> = {
                [K in keyof T as V extends T[K] ? K : never]: T[K];
            };

            export type optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
        }

        export type require<T extends Record<string, unknown>, K extends keyof T> = T & {
            [k in K]-?: NonNullable<T[k]>;
        };

        export type Values<T> = T[keyof T];

        export type override<T, Override extends Record<PropertyKey, unknown>> = Omit<
            T,
            keyof Override
        > &
            Override;
    }

    export type PromiseOrImmediate<T> = T | Promise<T>;

    export namespace Zod {
        export type Shape<T extends Record<string, any>> = {
            [K in keyof T]?: z.ZodType<T[K], any>;
        } & Record<string, z.ZodType>;

        export type InputShape<T extends Record<string, any>> = {
            [K in keyof T]: z.ZodType<any, T[K]>;
        };
    }
    // & Record<string, z.ZodType>
    export type NonNullFields<T extends Record<string, unknown>, K extends keyof T> = {
        [Key in keyof T]: Key extends K ? NonNullable<T[Key]> : T[Key];
    } & Required<Pick<T, K>>;

    export type MaybeReadonly<T> = T | Readonly<T>;

    export type SingleOrMultiple<T> = T | Array<T>;

    /** Generic pagination type used across the application */
    export type Pagination = {
        /** Current page number (1-based) */
        page: number;
        /** Number of items per page */
        size: number;
    };

    export type cast<$From, $To> = $To extends $From ? $To : never;
    export function cast<$To>() {
        return <$From>(value: $From): ts.cast<$From, $To> =>
            value as unknown as ts.cast<$From, $To>;
    }

    declare const $phantomTag: unique symbol;
    /** Useful to tag/brand certain types that are difficult for typescript to infer. */
    export type PhantomTag<T, $PhantomData> = T & { [$phantomTag]?: $PhantomData };
    export declare namespace PhantomTag {
        export type infer<T> = T extends PhantomTag<any, infer $PhantomData> ? $PhantomData : never;
    }

    export namespace Tuple {
        type __createTupleWithLength<
            T,
            N extends number,
            R extends T[] = [],
        > = R['length'] extends N ? R : __createTupleWithLength<T, N, [...R, T]>;

        export type WithLength<T, $N extends number> = __createTupleWithLength<T, $N>;
        export type WithType<T> = readonly [T, ...rest: T[]];
        /** Creates a tuple of length N where every slot is T. */
        type Repeat<T, N extends number, Acc extends T[] = []> = Acc['length'] extends N
            ? Acc
            : Repeat<T, N, [...Acc, T]>;

        /**
         * Maps a union to a tuple whose length equals the number of union members,
         * with every slot typed as the full union.
         *
         * This avoids the non-deterministic ordering of a naive union-to-tuple
         * conversion while still giving you a fixed-length tuple that enforces
         * exhaustiveness (e.g. for lookup tables or switch helpers).
         *
         * @example
         * type MyUnion = 'a' | 'b' | 'c';
         * type MyTuple = Tuple.fromUnion<MyUnion>; // [MyUnion, MyUnion, MyUnion]
         *
         * const items: MyTuple = ['a', 'b', 'c']; // ✅
         * const bad: MyTuple = ['a', 'b'];         // ❌ wrong length
         */
        export type fromUnion<U> = Repeat<U, Union.size<U>>;

        export function fromUnion<T>(...entries: Tuple.fromUnion<T>): Tuple.fromUnion<T> {
            return entries;
        }
    }

    export type AsyncFn<$Args extends unknown[], $Result> = (...args: $Args) => Promise<$Result>;
}
